import React, { useState, useEffect, useRef } from 'react';
import { buildMenuItems, buildOpeningHubText } from './jpBuanaChatContent';
import { PROFIL_JP_DOC, VISI_MISI_JP_DOC, FOUNDER_JP_DOC, KEUNGGULAN_JP_DOC } from './jpBuanaDocBody';
import { JPB, JPB_TW } from './jpBuanaTheme';

/** Kartu info — palet brand JP Buana (jpbuana.com) */
const InfoCard = ({ icon, title, children }) => (
  <div
    style={{
      background: JPB.cardGradient,
      border: `1px solid ${JPB.borderSolid}`,
      borderRadius: JPB.radiusLg,
      padding: '16px',
      marginBottom: '10px',
      boxShadow: JPB.shadow,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
      <span style={{ fontSize: '20px' }}>{icon}</span>
      <span style={{ fontWeight: '700', fontSize: '14px', color: JPB.navy800 }}>{title}</span>
    </div>
    <div style={{ fontSize: '13px', color: JPB.textPrimary, lineHeight: '1.6' }}>{children}</div>
  </div>
);

const Badge = ({ children }) => (
  <span
    style={{
      display: 'inline-block',
      background: JPB.goldSoft,
      color: JPB.goldInk,
      borderRadius: '999px',
      padding: '2px 10px',
      fontSize: '12px',
      fontWeight: '600',
      margin: '2px 3px',
    }}
  >
    {children}
  </span>
);

const ScheduleCard = ({ icon, title, day, time }) => (
  <div
    style={{
      background: JPB.ivory,
      border: `1px solid ${JPB.borderSolid}`,
      borderRadius: JPB.radius,
      padding: '14px',
      flex: 1,
      minWidth: '140px',
    }}
  >
    <div style={{ fontSize: '22px', marginBottom: '6px' }}>{icon}</div>
    <div style={{ fontWeight: '700', fontSize: '13px', color: JPB.navy800, marginBottom: '4px' }}>{title}</div>
    <div style={{ fontSize: '12px', color: JPB.blue700, fontWeight: '600' }}>{day}</div>
    <div style={{ fontSize: '12px', color: JPB.textPrimary }}>{time}</div>
  </div>
);

const BonusCard = ({ number, title, detail }) => (
  <div
    style={{
      background: JPB.white,
      border: `1px solid ${JPB.borderSolid}`,
      borderRadius: JPB.radius,
      padding: '12px 14px',
      borderLeft: `4px solid ${JPB.blue700}`,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
      <div
        style={{
          background: JPB.blue700,
          color: JPB.white,
          borderRadius: '50%',
          width: '24px',
          height: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '12px',
          fontWeight: '700',
          flexShrink: 0,
        }}
      >
        {number}
      </div>
      <div>
        <div style={{ fontWeight: '700', fontSize: '13px', color: JPB.navy800, marginBottom: '3px' }}>{title}</div>
        <div style={{ fontSize: '12px', color: JPB.textPrimary, lineHeight: '1.5' }}>{detail}</div>
      </div>
    </div>
  </div>
);

const TestimonyCard = ({ name, product, text, avatar }) => (
  <div
    style={{
      background: JPB.white,
      border: `1px solid ${JPB.borderSolid}`,
      borderRadius: JPB.radiusLg,
      padding: '14px',
      marginBottom: '10px',
      boxShadow: JPB.shadow,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
      <div
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          background: JPB.ivory,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          flexShrink: 0,
        }}
      >
        {avatar || '👤'}
      </div>
      <div>
        <div style={{ fontWeight: '700', fontSize: '13px', color: JPB.navy800 }}>{name}</div>
        <div style={{ fontSize: '11px', color: JPB.textMuted }}>{product}</div>
      </div>
      <div style={{ marginLeft: 'auto', color: JPB.gold500, fontSize: '14px' }}>⭐⭐⭐⭐⭐</div>
    </div>
    <p style={{ fontSize: '13px', color: JPB.textPrimary, lineHeight: '1.6', fontStyle: 'italic' }}>&ldquo;{text}&rdquo;</p>
  </div>
);

const FAQItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ border: `1px solid ${JPB.borderSolid}`, borderRadius: JPB.radius, marginBottom: '8px', overflow: 'hidden' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          width: '100%',
          padding: '12px 14px',
          background: open ? JPB.ivory : JPB.white,
          border: 'none',
          textAlign: 'left',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '13px',
          fontWeight: '600',
          color: JPB.navy800,
        }}
      >
        {question}
        <span
          style={{
            fontSize: '16px',
            transition: 'transform 0.2s',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          ▾
        </span>
      </button>
      {open && (
        <div style={{ padding: '10px 14px 14px', fontSize: '13px', color: JPB.textPrimary, lineHeight: '1.6', background: JPB.ivory }}>{answer}</div>
      )}
    </div>
  );
};

const OFFICE_MAPS_HREF = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
  'Green Lake City, Ruko Crown Blok E No. 21, Cipondoh, Tangerang'
)}`;
const CONTACT_PHONE_DISPLAY = '081776363738';
const CONTACT_PHONE_E164 = '+6281776363738';

function ContactInfo() {
  const [mapHover, setMapHover] = useState(false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <a
        href={OFFICE_MAPS_HREF}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Buka lokasi kantor di Google Maps"
        title="Buka di Google Maps"
        onMouseEnter={() => setMapHover(true)}
        onMouseLeave={() => setMapHover(false)}
        style={{
          background: mapHover ? JPB.warmWhite : JPB.ivory,
          border: `1px solid ${mapHover ? JPB.blue400 : JPB.borderSolid}`,
          borderRadius: JPB.radius,
          padding: '12px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          textDecoration: 'none',
          color: 'inherit',
          cursor: 'pointer',
          transition: 'background 0.15s ease, border-color 0.15s ease',
        }}
      >
        <span style={{ fontSize: '18px' }}>📍</span>
        <div>
          <div style={{ fontSize: '11px', color: JPB.textMuted, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Alamat</div>
          <div style={{ fontSize: '13px', color: JPB.navy800, fontWeight: '600' }}>Green Lake City, Ruko Crown Blok E No. 21</div>
          <div style={{ fontSize: '12px', color: JPB.textPrimary }}>Cipondoh – Tangerang</div>
        </div>
      </a>
    <div
      style={{
        background: JPB.ivory,
        border: `1px solid ${JPB.borderSolid}`,
        borderRadius: JPB.radius,
        padding: '12px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
      }}
    >
      <span style={{ fontSize: '18px' }}>🌐</span>
      <div>
        <div style={{ fontSize: '11px', color: JPB.textMuted, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Website</div>
        <a href="https://jpbuana.com/" target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', color: JPB.blue700, fontWeight: '600' }}>
          jpbuana.com
        </a>
      </div>
    </div>
    <div
      style={{
        background: JPB.ivory,
        border: `1px solid ${JPB.borderSolid}`,
        borderRadius: JPB.radius,
        padding: '12px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
      }}
    >
      <span style={{ fontSize: '18px' }}>📧</span>
      <div>
        <div style={{ fontSize: '11px', color: JPB.textMuted, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email</div>
        <a href="mailto:ceojpbuana@jpbuana.com" style={{ fontSize: '13px', color: JPB.blue700, fontWeight: '600' }}>
          ceojpbuana@jpbuana.com
        </a>
      </div>
    </div>
    <div
      style={{
        background: JPB.ivory,
        border: `1px solid ${JPB.borderSolid}`,
        borderRadius: JPB.radius,
        padding: '12px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
      }}
    >
      <span style={{ fontSize: '18px' }}>📞</span>
      <div>
        <div style={{ fontSize: '11px', color: JPB.textMuted, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Telepon</div>
        <a href={`tel:${CONTACT_PHONE_E164}`} style={{ fontSize: '13px', color: JPB.blue700, fontWeight: '600' }}>
          {CONTACT_PHONE_DISPLAY}
        </a>
      </div>
    </div>
  </div>
  );
}

const LINK_GELANG = 'https://gelang.jpbuana.com/';
const LINK_MINICON = 'https://minicon.jpbuana.com/';
const LINK_WORKSHOP = 'https://workshop.jpbuana.com/';
const LINK_AORA = 'https://bioelektromagnetiksocks.jpbuana.com/';
const LINK_UTAMA = 'https://jpbuana.com/';
const LINK_MANFAAT_MITRA_YT = 'https://www.youtube.com/watch?v=CegE7HyR3tY';
const LINK_AKTIVASI_INTUISI =
  'https://drive.google.com/file/d/1LEbrUVPPx0HrKZozZUiM3J3pbBZEw_TU/view?usp=drive_link';

function withSponsor(url, sponsor) {
  const safeSponsor = sponsor || 'demoaccount';
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}id=${encodeURIComponent(safeSponsor)}`;
}

function tentangEscapeHtml(s) {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function tentangLineToHtml(line) {
  const escaped = tentangEscapeHtml(line);
  return escaped.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
}

const TENTANG_VISI_MISI_LINE = /^(VISI|MISI)$/;
const TENTANG_LIST_LINE = /^[✨📱🌐🤖🎓🌿📿🛡⛽📍📧📞]/;

const tentangContactLinkStyle = {
  color: JPB.blue700,
  fontWeight: '700',
  textDecoration: 'none',
  borderBottom: `1px solid ${JPB.blue400}`,
};

/** Hanya 🌐 yang benar-benar URL (bukan baris seperti "🌐 Landing Page Digital"). */
function tentangParseWebUrlLine(line) {
  if (!line.startsWith('🌐')) return null;
  const rest = line.replace(/^🌐\s*/, '').trim();
  if (!/^(https?:\/\/|www\.)/i.test(rest)) return null;
  let href = rest;
  if (/^www\./i.test(href)) href = `https://${href}`;
  return { href, rest };
}

function tentangParseMailLine(line) {
  const m = line.match(/^📧\s+(\S+@\S+)$/);
  if (!m) return null;
  return { href: `mailto:${m[1]}`, rest: m[1] };
}

function tentangParsePhoneLine(line) {
  const m = line.match(/^📞\s+([\d\s().+-]+)$/);
  if (!m) return null;
  const digits = m[1].replace(/\D/g, '');
  if (digits.length < 9) return null;
  let intl = digits;
  if (digits.startsWith('0')) intl = `62${digits.slice(1)}`;
  else if (digits.startsWith('62')) intl = digits;
  else if (digits.startsWith('8')) intl = `62${digits}`;
  return { href: `tel:+${intl}`, rest: m[1].trim() };
}

/** Baris lanjutan alamat (tanpa emoji list / judul / kutipan). */
function tentangIsAddressContinuation(line) {
  const t = line.trim();
  if (!t) return false;
  if (TENTANG_LIST_LINE.test(t)) return false;
  if (t.startsWith('**')) return false;
  if (TENTANG_VISI_MISI_LINE.test(t)) return false;
  if (t === 'Your Success Community') return false;
  if (t.startsWith('"')) return false;
  if (t.length > 140) return false;
  return true;
}

function tentangIsColonSubheading(line, hasNext) {
  const t = line.trim();
  if (!hasNext || t.length > 96) return false;
  if (!t.endsWith(':')) return false;
  if (/^https?:/i.test(t)) return false;
  return true;
}

/** Blok alamat klik → Google Maps */
function TentangMapsAddressCard({ mapsHref, addrLines, itemKey }) {
  const [hover, setHover] = useState(false);
  return (
    <a
      href={mapsHref}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Buka lokasi di Google Maps"
      title="Buka di Google Maps"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'block',
        marginBottom: '10px',
        padding: '10px 12px',
        background: hover ? JPB.warmWhite : JPB.ivory,
        border: `1px solid ${hover ? JPB.blue400 : JPB.borderSolid}`,
        borderRadius: JPB.radius,
        textDecoration: 'none',
        color: JPB.textPrimary,
        fontSize: '13px',
        lineHeight: 1.55,
        cursor: 'pointer',
        transition: 'background 0.15s ease, border-color 0.15s ease',
      }}
    >
      {addrLines.map((ln, ai) => (
        <div key={`${itemKey}-a${ai}`} style={{ marginBottom: ai < addrLines.length - 1 ? 4 : 0 }}>
          {ln}
        </div>
      ))}
    </a>
  );
}

/** Konten panjang Tentang — blok, judul VISI/MISI, subjudul bertitik dua, daftar emoji, kutipan, dan **bold**. */
function TentangPolishedBody({ text }) {
  const blocks = text.trim().split(/\n\n+/);
  const sectionTitleStyle = {
    fontSize: '11px',
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    fontWeight: '800',
    color: JPB.blue700,
    marginBottom: '10px',
    marginTop: '2px',
  };
  const subheadStyle = {
    fontWeight: '700',
    fontSize: '12px',
    color: JPB.navy800,
    marginBottom: '8px',
    marginTop: '6px',
    lineHeight: 1.45,
  };
  const taglineBox = {
    marginTop: '4px',
    marginBottom: '12px',
    background: `linear-gradient(135deg, ${JPB.warmWhite} 0%, ${JPB.goldSoft} 100%)`,
    borderRadius: JPB.radius,
    padding: '12px 14px',
    textAlign: 'center',
    border: `1px solid ${JPB.borderSolid}`,
    fontWeight: '700',
    fontSize: '15px',
    fontStyle: 'italic',
    color: JPB.navy800,
  };
  const quoteBox = {
    margin: '10px 0',
    padding: '12px 14px',
    borderLeft: `4px solid ${JPB.blue700}`,
    background: JPB.ivory,
    borderRadius: '0 10px 10px 0',
    fontSize: '13px',
    fontStyle: 'italic',
    color: JPB.textPrimary,
    lineHeight: 1.65,
  };

  return (
    <div
      style={{
        maxHeight: 'min(70vh, 520px)',
        overflowY: 'auto',
        paddingRight: '8px',
      }}
    >
      {blocks.map((block, bi) => {
        const lines = block.split('\n').map((l) => l.trim()).filter(Boolean);
        const nodes = [];
        const rowMargin = { marginBottom: '6px', fontSize: '13px', lineHeight: 1.55, color: JPB.textPrimary };
        for (let li = 0; li < lines.length; li++) {
          const line = lines[li];
          const key = `${bi}-${li}`;
          if (TENTANG_VISI_MISI_LINE.test(line)) {
            nodes.push(
              <div key={key} style={sectionTitleStyle}>
                {line}
              </div>
            );
            continue;
          }
          if (line === 'Your Success Community') {
            nodes.push(
              <div key={key} style={taglineBox}>
                &ldquo;Your Success Community&rdquo;
              </div>
            );
            continue;
          }
          if (line.startsWith('"') && line.length > 14) {
            nodes.push(
              <div key={key} style={quoteBox} dangerouslySetInnerHTML={{ __html: tentangLineToHtml(line) }} />
            );
            continue;
          }
          if (line.startsWith('📍')) {
            const addrLines = [line];
            let j = li + 1;
            while (j < lines.length && tentangIsAddressContinuation(lines[j])) {
              addrLines.push(lines[j]);
              j++;
            }
            const mapQuery = addrLines
              .map((l, idx) => (idx === 0 ? l.replace(/^📍\s*/, '').trim() : l.trim()))
              .filter(Boolean)
              .join(', ');
            const mapsHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`;
            nodes.push(
              <TentangMapsAddressCard key={key} mapsHref={mapsHref} addrLines={addrLines} itemKey={key} />
            );
            li = j - 1;
            continue;
          }
          const webLink = tentangParseWebUrlLine(line);
          if (webLink) {
            nodes.push(
              <div key={key} style={rowMargin}>
                <span>🌐 </span>
                <a href={webLink.href} target="_blank" rel="noopener noreferrer" style={tentangContactLinkStyle}>
                  {webLink.rest}
                </a>
              </div>
            );
            continue;
          }
          const mailLink = tentangParseMailLine(line);
          if (mailLink) {
            nodes.push(
              <div key={key} style={rowMargin}>
                <span>📧 </span>
                <a href={mailLink.href} style={tentangContactLinkStyle}>
                  {mailLink.rest}
                </a>
              </div>
            );
            continue;
          }
          const phoneLink = tentangParsePhoneLine(line);
          if (phoneLink) {
            nodes.push(
              <div key={key} style={rowMargin}>
                <span>📞 </span>
                <a href={phoneLink.href} style={tentangContactLinkStyle}>
                  {phoneLink.rest}
                </a>
              </div>
            );
            continue;
          }
          if (TENTANG_LIST_LINE.test(line)) {
            nodes.push(
              <div
                key={key}
                style={{
                  marginBottom: '6px',
                  fontSize: '13px',
                  lineHeight: 1.55,
                  color: JPB.textPrimary,
                }}
              >
                <span dangerouslySetInnerHTML={{ __html: tentangLineToHtml(line) }} />
              </div>
            );
            continue;
          }
          if (tentangIsColonSubheading(line, Boolean(lines[li + 1]))) {
            nodes.push(
              <div key={key} style={subheadStyle} dangerouslySetInnerHTML={{ __html: tentangLineToHtml(line) }} />
            );
            continue;
          }
          nodes.push(
            <p
              key={key}
              style={{ margin: '0 0 10px', fontSize: '13px', lineHeight: 1.65, color: JPB.textPrimary }}
              dangerouslySetInnerHTML={{ __html: tentangLineToHtml(line) }}
            />
          );
        }
        return (
          <div
            key={bi}
            style={{
              marginBottom: bi < blocks.length - 1 ? 14 : 0,
              paddingBottom: bi < blocks.length - 1 ? 14 : 0,
              borderBottom: bi < blocks.length - 1 ? `1px solid ${JPB.borderSolid}` : 'none',
            }}
          >
            {nodes}
          </div>
        );
      })}
    </div>
  );
}

const produkLandingLinkStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '10px',
  textAlign: 'left',
  background: JPB.ivory,
  border: `1px solid ${JPB.borderSolid}`,
  color: JPB.navy800,
  fontWeight: '700',
  padding: '12px 14px',
  borderRadius: JPB.radius,
  textDecoration: 'none',
  fontSize: '13px',
};

const produkLandingLinkLabelStyle = {
  flex: '1 1 auto',
  minWidth: 0,
  textAlign: 'left',
  lineHeight: 1.35,
};

const produkLandingLinkArrowStyle = {
  flexShrink: 0,
  fontWeight: '700',
  color: JPB.blue700,
};

const PANELS = {
  profil: (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <InfoCard icon="🏢" title="Profil JP Buana">
        <TentangPolishedBody text={PROFIL_JP_DOC} />
      </InfoCard>
      <p style={{ fontSize: '12px', color: JPB.textMuted, marginTop: '2px' }}>
        Pilih topik lain atau <strong>Menu Utama</strong> lewat tombol di bawah.
      </p>
    </div>
  ),

  visiMisi: (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <InfoCard icon="🎯" title="Visi & Misi JP Buana">
        <TentangPolishedBody text={VISI_MISI_JP_DOC} />
      </InfoCard>
      <p style={{ fontSize: '12px', color: JPB.textMuted, marginTop: '2px' }}>
        Pilih topik lain atau <strong>Menu Utama</strong> lewat tombol di bawah.
      </p>
    </div>
  ),

  founder: (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <InfoCard icon="📖" title="Kisah Founder">
        <TentangPolishedBody text={FOUNDER_JP_DOC} />
      </InfoCard>
      <p style={{ fontSize: '12px', color: JPB.textMuted, marginTop: '2px' }}>
        Pilih topik lain atau <strong>Menu Utama</strong> lewat tombol di bawah.
      </p>
    </div>
  ),

  keunggulan: (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <InfoCard icon="⚡" title="Keunggulan Sistem JP Buana">
        <TentangPolishedBody text={KEUNGGULAN_JP_DOC} />
      </InfoCard>
      <p style={{ fontSize: '12px', color: JPB.textMuted, marginTop: '2px' }}>
        Pilih topik lain atau <strong>Menu Utama</strong> lewat tombol di bawah.
      </p>
    </div>
  ),

  peluang: (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <InfoCard icon="💼" title="Peluang Bisnis JP Buana">
        <p style={{ fontSize: '13px', color: JPB.textPrimary, lineHeight: 1.65, marginBottom: '12px' }}>
          JP Buana membuka <strong>peluang usaha</strong> dalam ekosistem penjualan langsung yang didukung <strong>sistem digital</strong>, edukasi, dan komunitas — sehingga mitra dapat berkembang dengan cara yang fleksibel dan relevan dengan zaman.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '13px', color: JPB.textPrimary, lineHeight: 1.55 }}>
            <span style={{ color: JPB.blue700, flexShrink: 0 }}>✓</span>
            <span>Produk dan program pengembangan diri dalam <strong>satu ekosistem terintegrasi</strong>.</span>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '13px', color: JPB.textPrimary, lineHeight: 1.55 }}>
            <span style={{ color: JPB.blue700, flexShrink: 0 }}>✓</span>
            <span>Peluang membangun jaringan dengan dukungan materi, mentoring, dan kanal digital.</span>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '13px', color: JPB.textPrimary, lineHeight: 1.55 }}>
            <span style={{ color: JPB.blue700, flexShrink: 0 }}>✓</span>
            <span>Struktur apresiasi dan reward dirancang transparan — detail lewat admin/sponsor.</span>
          </div>
        </div>
        <p style={{ fontSize: '12px', fontWeight: '700', color: JPB.navy800, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Materi peluang bisnis
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
          <a href={LINK_MANFAAT_MITRA_YT} target="_blank" rel="noopener noreferrer" style={produkLandingLinkStyle}>
            <span style={produkLandingLinkLabelStyle}>▶️ Manfaat &amp; Keuntungan Mitra Bisnis JPB</span>
            <span style={produkLandingLinkArrowStyle} aria-hidden="true">
              →
            </span>
          </a>
          <a href={LINK_AKTIVASI_INTUISI} target="_blank" rel="noopener noreferrer" style={produkLandingLinkStyle}>
            <span style={produkLandingLinkLabelStyle}>🎥 Rekaman Aktivasi Intuisi (23 Juli 2026)</span>
            <span style={produkLandingLinkArrowStyle} aria-hidden="true">
              →
            </span>
          </a>
        </div>
        <div
          style={{
            background: JPB.warmWhite,
            border: `1px solid ${JPB.borderSolid}`,
            borderRadius: '8px',
            padding: '10px 12px',
            fontSize: '12px',
            color: JPB.navy800,
            lineHeight: 1.55,
          }}
        >
          Untuk <strong>detail kompensasi</strong>, materi legal terbaru, atau diskusi personal: gunakan menu <strong>Hubungi Admin</strong> atau kunjungi{' '}
          <a href={LINK_UTAMA} target="_blank" rel="noopener noreferrer" style={{ color: JPB.blue700, fontWeight: 700 }}>
            jpbuana.com
          </a>
          .
        </div>
      </InfoCard>
    </div>
  ),

  // TODO(client-final): Copy + `LINK_WORKSHOP` + badge kelas; landing tidak terintegrasi WA (sub-website workshop)
  workshop: (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <InfoCard icon="🎓" title="Workshop Menciptakan Realita Impian">
        <p style={{ fontSize: '13px', color: JPB.textPrimary, lineHeight: 1.65 }}>
          Program workshop JP Buana — informasi lengkap, jadwal, dan pendaftaran di <strong>landing resmi</strong> (sub-website workshop).
        </p>
        <p style={{ marginTop: '8px', fontSize: '12px', color: JPB.textMuted, lineHeight: 1.5 }}>
          Halaman landing dibuka di browser dan <strong>tidak terintegrasi ke WhatsApp</strong> mana pun.
        </p>
        <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          <Badge>KuantumOASE</Badge>
          <Badge>B4 FORMATS</Badge>
          <Badge>COMMUNION SYMBOL</Badge>
        </div>
        <a href={LINK_WORKSHOP} target="_blank" rel="noopener noreferrer" style={{ ...produkLandingLinkStyle, marginTop: '14px' }}>
          <span style={produkLandingLinkLabelStyle}>🎓 Buka landing workshop resmi</span>
          <span style={produkLandingLinkArrowStyle} aria-hidden="true">
            →
          </span>
        </a>
      </InfoCard>
    </div>
  ),

  mentoring: (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <InfoCard icon="🤝" title="Mentoring Mingguan JP Buana">
        <p style={{ fontSize: '13px', color: JPB.textPrimary, lineHeight: 1.65, marginBottom: '12px' }}>
          Sesi rutin untuk mitra: materi bisnis, sharing praktik, dan tanya jawab bersama jaringan — mendukung konsistensi belajar dan eksekusi di lapangan.
        </p>
        <div style={{ marginTop: '4px' }}>
          <div style={{ fontWeight: '600', color: JPB.navy800, marginBottom: '6px', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Jadwal terkonfirmasi
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <span style={{ color: JPB.blue700, flexShrink: 0 }}>✓</span>
            <span style={{ fontSize: '13px', color: JPB.textPrimary, lineHeight: 1.55 }}>
              Setiap <strong>Rabu</strong>, 19.30 – 21.00 WIB via Zoom
            </span>
          </div>
        </div>
        <div style={{ marginTop: '12px', background: JPB.goldSoft, border: `1px solid ${JPB.gold400}`, borderRadius: JPB.radius, padding: '10px 12px', fontSize: '12px', color: JPB.goldInk }}>
          Link Zoom dibagikan lewat sponsor atau grup resmi. Rekaman sesi tersedia di menu <strong>Peluang Bisnis</strong>.
        </div>
      </InfoCard>
    </div>
  ),

  // TODO(client-final): Tiga kutipan testimoni resmi (gelang & topi, minicon, workshop) — ganti placeholder di `TestimonyCard`.
  testimoni: (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <InfoCard icon="💬" title="Testimoni">
        <p style={{ fontSize: '13px', color: JPB.textPrimary, lineHeight: 1.6, marginBottom: '12px' }}>
          Tiga lini sesuai materi resmi: <strong>Gelang &amp; Topi (Pesona Aura)</strong>, <strong>Minicon</strong>, dan <strong>Workshop Menciptakan Realita Impian</strong>. Kutipan di bawah dapat diganti tim dengan testimonial final kapan saja.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <TestimonyCard
            name="Kutipan mitra"
            product="Gelang & Topi (Pesona Aura)"
            text="Placeholder — kutipan resmi dari tim akan ditampilkan di sini."
            avatar="💍"
          />
          <TestimonyCard
            name="Kutipan mitra"
            product="Minicon"
            text="Placeholder — kutipan resmi dari tim akan ditampilkan di sini."
            avatar="⚡"
          />
          <TestimonyCard
            name="Kutipan mitra"
            product="Workshop MRI"
            text="Placeholder — kutipan resmi dari tim akan ditampilkan di sini."
            avatar="🎓"
          />
        </div>
      </InfoCard>
    </div>
  ),

  // TODO(client-confirm): Jam & copy jadwal Zoom vs dokumen final (Rabu / Jumat 19.30–21.00)
  jadwal: (
    <div>
      <InfoCard icon="📅" title="Jadwal Zoom JP Buana">
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '8px' }}>
          <ScheduleCard icon="🎙️" title="Mentoring" day="Setiap Rabu" time="19.30 – 21.00 WIB" />
          <ScheduleCard icon="📊" title="Presentasi Bisnis" day="Setiap Jumat" time="19.30 – 21.00 WIB" />
        </div>
        <div style={{ marginTop: '12px', background: JPB.goldSoft, border: `1px solid ${JPB.gold400}`, borderRadius: JPB.radius, padding: '10px 12px', fontSize: '12px', color: JPB.goldInk }}>
          Link Zoom dibagikan lewat sponsor atau grup resmi.
        </div>
      </InfoCard>
    </div>
  ),

  // TODO(client-final): Daftar FAQ per kategori (gelang & topi, minicon, kelas, umum) — selaraskan dengan materi final
  faq: (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <InfoCard icon="❓" title="FAQ / Pertanyaan umum">
        <p style={{ fontSize: '13px', color: JPB.textPrimary, lineHeight: 1.6, marginBottom: '12px' }}>
          Jawaban ringkas per kategori produk dan layanan. Ketuk pertanyaan untuk membuka jawaban.
        </p>
        <div style={{ fontSize: '12px', fontWeight: '700', color: JPB.blue700, marginBottom: '6px', textTransform: 'uppercase' }}>Gelang dan topi</div>
        <FAQItem question="Di mana detail produk?" answer={`Landing resmi: ${LINK_GELANG}`} />
        <FAQItem question="Untuk siapa produk ini?" answer="Bagi yang ingin mendukung gaya hidup dan kualitas hidup sehari-hari." />

        <div style={{ fontSize: '12px', fontWeight: '700', color: JPB.blue700, marginTop: '14px', marginBottom: '6px', textTransform: 'uppercase' }}>Minicon</div>
        <FAQItem question="Di mana penjelasan & testimoni Minicon?" answer={`Landing resmi: ${LINK_MINICON}`} />
        <FAQItem question="Cara membeli?" answer="Lewat menu Belanja Produk di chat ini atau website utama." />

        <div style={{ fontSize: '12px', fontWeight: '700', color: JPB.blue700, marginTop: '14px', marginBottom: '6px', textTransform: 'uppercase' }}>JPB AORA (Kaos Kaki)</div>
        <FAQItem question="Di mana detail produk JPB AORA?" answer={`Landing resmi: ${LINK_AORA}`} />
        <FAQItem question="Apakah JPB AORA menyembuhkan penyakit?" answer="Tidak. JPB AORA adalah pendamping kenyamanan dan kesehatan holistik harian — bukan pengganti diagnosis atau perawatan medis." />

        <div style={{ fontSize: '12px', fontWeight: '700', color: JPB.blue700, marginTop: '14px', marginBottom: '6px', textTransform: 'uppercase' }}>Kelas</div>
        <FAQItem question="Program workshop MRI?" answer={`Informasi & pendaftaran: ${LINK_WORKSHOP}`} />
        <FAQItem question="Kelas sertifikasi (B4Formats, Kuantum Oase, Communion Symbol)?" answer="Ikuti informasi di landing workshop atau arahan admin/sponsor." />
        <FAQItem question="Ada rekaman mentoring?" answer={`Rekaman Aktivasi Intuisi (23 Juli 2026): ${LINK_AKTIVASI_INTUISI}`} />

        <div style={{ fontSize: '12px', fontWeight: '700', color: JPB.blue700, marginTop: '14px', marginBottom: '6px', textTransform: 'uppercase' }}>Umum</div>
        <FAQItem question="Apa itu JP Buana?" answer={`Portal utama: ${LINK_UTAMA} — produk, edukasi, dan peluang usaha terintegrasi.`} />
        <FAQItem question="Cara daftar member?" answer="Pilih Daftar Member → produk → alamat → data diri; tim menghubungi setelah verifikasi." />
      </InfoCard>
    </div>
  ),

  kontak: (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <InfoCard icon="📞" title="Hubungi Admin JP Buana">
        <p>Tim siap membantu:</p>
        <div style={{ marginTop: '10px' }}>
          <ContactInfo />
        </div>
      </InfoCard>
    </div>
  ),

  bonus: (
    <div>
      <InfoCard icon="💰" title="Bonus & Rewards JP Buana">
        <p style={{ marginBottom: '12px' }}>Ringkasan sistem reward:</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <BonusCard number="1" title="Bonus Sponsor" detail="Orang ke-1: Rp 350.000 | Ke-2: Rp 450.000 | Ke-3+: Rp 600.000" />
          <BonusCard number="2" title="Bonus Pasangan" detail="Rp 150.000/pasangan — maks Rp 10.000.000/hari" />
          <BonusCard number="3" title="Bonus Titik" detail="Rp 10.000/titik — hingga 20 level" />
          <BonusCard number="4" title="Bonus Generasi" detail="Rp 15.000/titik — hingga 12 level" />
          <BonusCard number="5" title="Bonus Promo Founder" detail="Bergabung di 12 bulan pertama" />
          <BonusCard number="6" title="Bonus Plan B (Maintenance)" detail="Maintenance Rp 3.300.000" />
        </div>
      </InfoCard>
    </div>
  ),
};

/** Panel intro Produk + landing (sponsor dinamis). */
function getProdukShowcasePanel(sponsor) {
  const s = sponsor || 'demoaccount';
  const aoraHref = withSponsor(LINK_AORA, s);
  const gelangHref = withSponsor(LINK_GELANG, s);
  const miniconHref = withSponsor(LINK_MINICON, s);
  return (
    <div>
      <InfoCard icon="📦" title="Produk JP Buana">
        <p>
          Website resmi:{' '}
          <a href={LINK_UTAMA} target="_blank" rel="noopener noreferrer" style={{ color: JPB.blue700, fontWeight: 700 }}>
            jpbuana.com
          </a>
        </p>
        <p style={{ marginTop: '10px', fontSize: '12px', color: JPB.textMuted, lineHeight: 1.5 }}>
          Landing produk berikut dibuka di browser dan <strong>tidak terintegrasi ke WhatsApp</strong> mana pun.
        </p>
        <div style={{ marginTop: '10px', background: JPB.warmWhite, border: `1px solid ${JPB.borderSolid}`, borderRadius: JPB.radius, padding: '10px 12px', fontSize: '13px', color: JPB.navy800 }}>
          Anda direferensikan atas sponsor: <strong>{s}</strong>
        </div>
      </InfoCard>
      <InfoCard icon="🔗" title="Landing produk">
        <p style={{ marginBottom: '10px', fontSize: '12px', color: JPB.textPrimary }}>Buka situs resmi tiap lini:</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <a href={aoraHref} target="_blank" rel="noopener noreferrer" style={produkLandingLinkStyle}>
            <span style={produkLandingLinkLabelStyle}>🧦 JPB AORA (Kaos Kaki Kesehatan)</span>
            <span style={produkLandingLinkArrowStyle} aria-hidden="true">
              →
            </span>
          </a>
          <a href={miniconHref} target="_blank" rel="noopener noreferrer" style={produkLandingLinkStyle}>
            <span style={produkLandingLinkLabelStyle}>⚡ Minicon</span>
            <span style={produkLandingLinkArrowStyle} aria-hidden="true">
              →
            </span>
          </a>
          <a href={gelangHref} target="_blank" rel="noopener noreferrer" style={produkLandingLinkStyle}>
            <span style={produkLandingLinkLabelStyle}>💍 Gelang Pesona Aura</span>
            <span style={produkLandingLinkArrowStyle} aria-hidden="true">
              →
            </span>
          </a>
        </div>
      </InfoCard>
    </div>
  );
}

/** Panel workshop dengan link sponsor dinamis. */
function getWorkshopPanel(sponsor) {
  const workshopHref = withSponsor(LINK_WORKSHOP, sponsor);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <InfoCard icon="🎓" title="Workshop Menciptakan Realita Impian">
        <p style={{ fontSize: '13px', color: JPB.textPrimary, lineHeight: 1.65 }}>
          Program workshop JP Buana — informasi lengkap, jadwal, dan pendaftaran di <strong>landing resmi</strong> (sub-website workshop).
        </p>
        <p style={{ marginTop: '8px', fontSize: '12px', color: JPB.textMuted, lineHeight: 1.5 }}>
          Halaman landing dibuka di browser dan <strong>tidak terintegrasi ke WhatsApp</strong> mana pun.
        </p>
        <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          <Badge>KuantumOASE</Badge>
          <Badge>B4 FORMATS</Badge>
          <Badge>COMMUNION SYMBOL</Badge>
        </div>
        <a href={workshopHref} target="_blank" rel="noopener noreferrer" style={{ ...produkLandingLinkStyle, marginTop: '14px' }}>
          <span style={produkLandingLinkLabelStyle}>🎓 Buka landing workshop resmi</span>
          <span style={produkLandingLinkArrowStyle} aria-hidden="true">
            →
          </span>
        </a>
      </InfoCard>
    </div>
  );
}

/**
 * @param {string} contentKey
 * @returns {React.ReactNode | null}
 */
function getRichChatPanel(contentKey) {
  return PANELS[contentKey] ?? null;
}

// Notification Component
const Notification = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 5000);

    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`notification ${type}`}>
      <div className="notification-content">
        <span className="notification-icon">
          {type === 'error' ? '❌' : type === 'success' ? '✅' : 'ℹ️'}
        </span>
        <span className="notification-message">{message}</span>
        <button className="notification-close" onClick={onClose}>×</button>
      </div>
    </div>
  );
};

// Shopping Cart Component
const ShoppingCart = ({ cartItems, onRemoveItem, onUpdateQuantity, onCheckout, onClose, onContinueShopping }) => {
  const totalPrice = cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);

  if (cartItems.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-md w-full p-6">
          <div className="text-center">
            <span className="text-6xl">🛒</span>
            <h3 className="text-xl font-semibold mt-4 mb-2">Keranjang Belanja Kosong</h3>
            <p className="text-gray-600 mb-6">Belum ada produk yang ditambahkan ke keranjang.</p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-jpb text-white rounded-md hover:bg-jpb-dark"
            >
              Tutup
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold">🛒 Keranjang Belanja</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
        </div>

        <div className="p-6">
          {cartItems.map((item, index) => (
            <div key={index} className="flex items-center gap-4 border-b py-4">
              <img src={item.imageUrl} alt={item.title} className="w-16 h-16 object-cover rounded" />
              <div className="flex-1">
                <h3 className="font-semibold">{item.title}</h3>
                <p className="text-jpb font-bold">Rp {item.price.toLocaleString('id-ID')}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                  className="w-8 h-8 bg-gray-200 rounded-full hover:bg-gray-300"
                >
                  -
                </button>
                <span className="w-8 text-center">{item.quantity}</span>
                <button
                  onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                  className="w-8 h-8 bg-gray-200 rounded-full hover:bg-gray-300"
                >
                  +
                </button>
              </div>
              <button
                onClick={() => onRemoveItem(item.id)}
                className="text-red-500 hover:text-red-700"
              >
                🗑️
              </button>
            </div>
          ))}

          <div className="mt-6 pt-4 border-t">
            <div className="flex justify-between text-xl font-bold mb-6">
              <span>Total:</span>
              <span className="text-jpb">Rp {totalPrice.toLocaleString('id-ID')}</span>
            </div>

            <div className="flex gap-3">
              <button
                onClick={onContinueShopping}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
              >
                Lanjutkan Belanja
              </button>
              <button
                onClick={onCheckout}
                className="flex-1 px-4 py-2 bg-jpb text-white rounded-md hover:bg-jpb-dark"
              >
                Checkout
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Checkout Form Component
const CheckoutForm = ({ cartItems, onSubmit, onCancel, isSubmitting, sponsorName }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    province: '',
    postalCode: '',
    notes: ''
  });

  const totalPrice = cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.address) {
      alert('Harap isi nama, nomor telepon, dan alamat lengkap');
      return;
    }
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4">
          <h2 className="text-xl font-bold">📝 Formulir Pemesanan</h2>
          {sponsorName && (
            <p className="text-sm text-gray-600 mt-1">Sponsor: {sponsorName}</p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold mb-2">Ringkasan Pesanan:</h3>
            {cartItems.map((item, idx) => (
              <div key={idx} className="flex justify-between text-sm mb-1">
                <span>{item.title} x{item.quantity}</span>
                <span>Rp {(item.price * item.quantity).toLocaleString('id-ID')}</span>
              </div>
            ))}
            <div className="border-t mt-2 pt-2 font-bold flex justify-between">
              <span>Total:</span>
              <span className="text-jpb">Rp {totalPrice.toLocaleString('id-ID')}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Nama Lengkap *</label>
              <input
                type="text"
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-jpb"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-jpb"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Nomor Telepon *</label>
              <input
                type="tel"
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-jpb"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Alamat Lengkap *</label>
              <textarea
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-jpb"
                rows="2"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Kota</label>
              <input
                type="text"
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-jpb"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Provinsi</label>
              <input
                type="text"
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-jpb"
                value={formData.province}
                onChange={(e) => setFormData({ ...formData, province: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Kode Pos</label>
              <input
                type="text"
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-jpb"
                value={formData.postalCode}
                onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Catatan (opsional)</label>
              <textarea
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-jpb"
                rows="2"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder=""
              />
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-jpb text-white rounded-md hover:bg-jpb-dark disabled:opacity-50"
            >
              {isSubmitting ? 'Memproses...' : 'Konfirmasi Pesanan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main App component
const App = () => {
  // State to store messages
  const [messages, setMessages] = useState([]);
  // State to track the ID of the message currently awaiting input from the user
  const [activePromptId, setActivePromptId] = useState(null);
  // State to store the current input value for the active prompt (for text prompts)
  const [currentPromptInput, setCurrentPromptInput] = useState('');
  // Ref for the messages container to enable auto-scrolling
  const messagesEndRef = useRef(null);
  // New state to indicate if the bot is typing/loading
  const [isBotTyping, setIsBotTyping] = useState(false);
  // New state for registration form inputs
  const [registrationData, setRegistrationData] = useState({
    name: '',
    email: '',
    phone: '',
    username: '',
    reg_member_sponsor: '',
    reg_member_username: '',
    reg_member_name: '',
    reg_member_email: '',
    reg_member_phone: ''
  });
  // State untuk autosearch username
  const [usernameSearchResults, setUsernameSearchResults] = useState([]);
  const [isSearchingUsername, setIsSearchingUsername] = useState(false);
  // State untuk produk yang dipilih dan data alamat
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [registrationStep, setRegistrationStep] = useState('product');
  const [addressData, setAddressData] = useState({
    street: '',
    city: '',
    province: '',
    postalCode: '',
    country: 'Indonesia'
  });
  // New state to manage which set of quick reply buttons is currently visible
  const [currentVisibleMenuItems, setCurrentVisibleMenuItems] = useState([]);
  // New state for dynamic bot name
  const [botName, setBotName] = useState('Tim CS');
  // NEW: State untuk notifikasi
  const [notification, setNotification] = useState(null);
  // NEW: State untuk sponsor dari URL parameter
  const [sponsorFromUrl, setSponsorFromUrl] = useState('');
  // NEW: State untuk validitas sponsor
  const [isSponsorValid, setIsSponsorValid] = useState(false);
  const [isValidatingSponsor, setIsValidatingSponsor] = useState(true);
  // State untuk tracking apakah registrasi sedang berlangsung
  const [isRegistrationFlowActive, setIsRegistrationFlowActive] = useState(false);
  // State untuk menyimpan nama customer
  const [customerName, setCustomerName] = useState('');

  // NEW: State untuk shopping cart
  const [cartItems, setCartItems] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);

  // NEW: Ref for unique message IDs
  const messageIdRef = useRef(0);

  // Function to scroll to the bottom of the messages container
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Helper function to extract YouTube video ID
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return '';
    const regExp = /(?:youtube\.com\/(?:[^\\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/|youtube\.com\/shorts\/)([^"&?\\/\s]{11})/i;
    const match = url.match(regExp);
    return match ? `https://www.youtube.com/embed/${match[1]}?autoplay=0&controls=1` : '';
  };

  // Fungsi untuk menampilkan notifikasi
  const showNotification = (message, type = 'error') => {
    setNotification({ message, type });
  };

  // Fungsi untuk menutup notifikasi
  const closeNotification = () => {
    setNotification(null);
  };

  // NEW: Shopping cart functions
  const addToCart = (product) => {
    setCartItems(prevItems => {
      const existingItem = prevItems.find(item => item.id === product.id);
      if (existingItem) {
        return prevItems.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prevItems, { ...product, quantity: 1 }];
    });
    showNotification(`✅ ${product.title} ditambahkan ke keranjang!`, 'success');
  };

  const removeFromCart = (productId) => {
    setCartItems(prevItems => prevItems.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
    } else {
      setCartItems(prevItems =>
        prevItems.map(item =>
          item.id === productId ? { ...item, quantity: newQuantity } : item
        )
      );
    }
  };

  // Fungsi untuk menyimpan transaksi belanja (termasuk untuk non-member)
  const saveTransaction = async (orderData, customerInfo, isMember = false) => {
    const transactionPayload = {
      transaction_type: 'shopping',
      is_member: isMember,
      sponsor: sponsorFromUrl || 'demoaccount', // PASTIKAN tercatat milik sponsor
      customer_name: customerInfo.name,
      customer_email: customerInfo.email || '',
      customer_phone: customerInfo.phone,
      shipping_address: {
        street: customerInfo.address,
        city: customerInfo.city || '',
        province: customerInfo.province || '',
        postal_code: customerInfo.postalCode || ''
      },
      items: orderData.items,
      total_amount: orderData.total_amount,
      transaction_date: new Date().toISOString(),
      notes: customerInfo.notes || ''
    };

    try {
      const response = await fetch('https://api.chat.jpbuana.com/save_transaction.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(transactionPayload),
      });

      return { success: response.ok, data: await response.json().catch(() => ({})) };
    } catch (error) {
      console.error('Error saving transaction:', error);
      // Simulasi sukses untuk demo
      return { success: true, data: { message: 'Transaction saved locally' } };
    }
  };

  const handleCheckout = async (orderData) => {
    setIsSubmittingOrder(true);

    const orderItems = cartItems.map(item => ({
      product_id: item.id,
      product_name: item.title,
      quantity: item.quantity,
      price: item.price,
      subtotal: item.price * item.quantity
    }));

    const totalAmount = cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);

    // Simpan transaksi dengan sponsor (demoaccount atau dari URL)
    const result = await saveTransaction(
      { items: orderItems, total_amount: totalAmount },
      orderData,
      false // Bukan member, hanya belanja biasa
    );

    if (result.success) {
      // Tampilkan ringkasan pesanan di chat dengan informasi sponsor
      const orderSummary = `🛍️ **Pesanan Berhasil!**\n\n` +
        `Terima kasih ${orderData.name} telah berbelanja di JP Buana.\n\n` +
        `**Detail Pesanan:**\n` +
        cartItems.map(item => `• ${item.title} x${item.quantity} = Rp ${(item.price * item.quantity).toLocaleString('id-ID')}`).join('\n') +
        `\n\n**Total:** Rp ${totalAmount.toLocaleString('id-ID')}\n` +
        `**Sponsor:** ${sponsorFromUrl || 'demoaccount'}\n\n` +
        `**Alamat Pengiriman:**\n${orderData.address}\n\n` +
        `Pesanan akan diproses dalam 1x24 jam. Tim kami akan menghubungi Anda melalui ${orderData.phone} untuk konfirmasi.`;

      await addBotMessageWithDelay(orderSummary, 500);

      // Kosongkan keranjang
      setCartItems([]);
      setShowCart(false);
      setShowCheckout(false);

      await addBotMessageWithDelay('Ada lagi yang bisa **Mimin** bantu? 😊', 700);
      const finalMenuItems = [...menuItems];
      setMessages(prevMessages => [
        ...prevMessages,
        { id: ++messageIdRef.current, type: 'quickReplyButtons', sender: 'user', items: finalMenuItems }
      ]);
      setCurrentVisibleMenuItems(finalMenuItems);

      showNotification('✅ Pesanan berhasil! Transaksi telah tercatat atas sponsor ' + (sponsorFromUrl || 'demoaccount'), 'success');
    } else {
      showNotification('Gagal menyimpan transaksi. Silakan coba lagi.', 'error');
    }

    setIsSubmittingOrder(false);
  };

  // Fungsi untuk autosearch username
  const searchUsername = async (username) => {
    if (username.length < 3) {
      setUsernameSearchResults([]);
      return;
    }

    setIsSearchingUsername(true);
    try {
      const response = await fetch(`https://api.chat.jpbuana.com/search-username?q=${encodeURIComponent(username)}`);
      if (response.ok) {
        const data = await response.json();
        setUsernameSearchResults(data.results || []);
      } else {
        setUsernameSearchResults([]);
      }
    } catch (error) {
      console.error('Error searching username:', error);
      setUsernameSearchResults([]);
    } finally {
      setIsSearchingUsername(false);
    }
  };

  // Handler untuk perubahan input username
  const handleUsernameChange = (e) => {
    const value = e.target.value;
    setRegistrationData({ ...registrationData, username: value, reg_member_username: value });
  };

  // Handler untuk memilih username dari hasil pencarian
  const handleUsernameSelect = (selectedUsername) => {
    setRegistrationData({ ...registrationData, username: selectedUsername, reg_member_username: selectedUsername });
    setUsernameSearchResults([]);
  };

  // Fungsi untuk memulai flow registrasi dengan produk
  const startRegistrationWithProduct = async () => {
    setIsRegistrationFlowActive(true);
    setRegistrationStep('product');

    await addBotMessageWithDelay('Silakan pilih produk untuk melanjutkan **pendaftaran member**:', 500);

    setMessages(prevMessages => [
      ...prevMessages,
      {
        id: ++messageIdRef.current,
        sender: 'bot',
        type: 'productGrid',
        hasAvatar: true,
        products: productsList,
        returnToItems: [],
        isRegistrationFlow: true
      }
    ]);
  };

  // Fungsi untuk menangani pemilihan produk dalam flow registrasi
  const handleProductSelectForRegistration = async (product) => {
    setSelectedProduct(product);
    setRegistrationStep('address');

    setIsBotTyping(true);
    await new Promise(resolve => setTimeout(resolve, 700));

    setMessages(prevMessages => [
      ...prevMessages.filter(msg => msg.type !== 'quickReplyButtons'),
      {
        id: ++messageIdRef.current,
        text: `${product.title} - Rp ${product.price.toLocaleString('id-ID')}`,
        sender: 'user',
        type: 'text',
        isUserResponse: true
      },
      {
        id: ++messageIdRef.current,
        text: `✅ Produk **"${product.title}"** dipilih.\n\nSilakan isi **alamat pengiriman**:`,
        sender: 'bot',
        type: 'text',
        hasAvatar: true
      },
      {
        id: ++messageIdRef.current,
        type: 'addressForm',
        sender: 'bot',
        hasAvatar: true,
        isFromRegistration: true,
        nextStepAfterAddress: 'form'
      }
    ]);

    setIsBotTyping(false);
  };

  // Fungsi untuk menangani submit alamat dalam flow registrasi
  const handleAddressSubmitForRegistration = async () => {
    if (!addressData.street || !addressData.city || !addressData.province || !addressData.postalCode) {
      showNotification('Harap isi semua field alamat untuk melanjutkan.', 'error');
      return;
    }

    setIsBotTyping(true);
    await new Promise(resolve => setTimeout(resolve, 700));

    setMessages(prevMessages => [
      ...prevMessages.filter(msg => msg.type !== 'quickReplyButtons' && msg.type !== 'addressForm'),
      {
        id: ++messageIdRef.current,
        text: `📍 ${addressData.street}, ${addressData.city}, ${addressData.province}, ${addressData.postalCode}`,
        sender: 'user',
        type: 'text',
        isUserResponse: true
      },
      {
        id: ++messageIdRef.current,
        text: '📍 Alamat tersimpan.\n\nSilakan lengkapi **data diri** untuk menyelesaikan pendaftaran member:',
        sender: 'bot',
        type: 'text',
        hasAvatar: true
      },
      {
        id: ++messageIdRef.current,
        type: 'form',
        sender: 'bot',
        hasAvatar: true,
        isFromRegistration: true,
        returnToItems: menuItems
      }
    ]);

    setRegistrationStep('form');
    setIsBotTyping(false);
  };

  // Daftar produk
  const productsList = [
    {
      id: 6,
      imageUrl: 'images/paket-minicon.png',
      title: 'PAKET MINICON',
      description: '1 Unit M12 dan 1 unit MM',
      fullDescription: 'Paket MINICON merupakan paket starter yang sempurna untuk memulai bisnis Anda. Termasuk 1 unit M12 dan 1 unit MM dengan kualitas terbaik.',
      price: 1500000
    },
    {
      id: 5,
      imageUrl: 'images/pesona-aura.png',
      title: 'PESONA AURA',
      description: '2 bh Gelang Aura dan 1 bh Topi Aura',
      fullDescription: 'Paket PESONA AURA menghadirkan gaya dan energi positif. Termasuk 2 buah Gelang Aura dan 1 buah Topi Aura yang dirancang dengan kualitas premium.',
      price: 1500000
    },
  ];

  // Tombol navigasi (harus ada sebelum menuItems — dipakai di handler & buildMenuItems tidak memuat ini)
  const mainMenuButton = { text: 'Menu Utama', icon: '🏠', isMainMenu: true };
  const backToProductsButton = { text: 'Kembali', icon: '⬅️', isBack: true };
  const cartButton = { text: `🛒 Keranjang (${cartItems.length})`, icon: '🛒', action: 'viewCart' };

  // Menu & narasi panjang — dokumen final klien (src/jpBuanaChatContent.js)
  const menuItems = buildMenuItems(productsList, sponsorFromUrl);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIfMobile = () => {
      const userAgent = navigator.userAgent;
      const mobileDevices = [
        /Android/i,
        /webOS/i,
        /iPhone/i,
        /iPad/i,
        /iPod/i,
        /BlackBerry/i,
        /Windows Phone/i
      ];

      const isMobileDevice = mobileDevices.some(device =>
        userAgent.match(device)
      );

      const isSmallScreen = window.innerWidth < 768;
      setIsMobile(isMobileDevice || isSmallScreen);
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);

    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  // Fungsi untuk memeriksa idParam dari URL dan memvalidasi sponsor
  useEffect(() => {
    const checkAndValidateIdParam = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const idParam = urlParams.get('id');

        if (idParam && idParam !== 'undefined' && idParam !== 'null') {
          const apiURL = `https://api.chat.jpbuana.com/chekid.php?id=${idParam}`;
          const response = await fetch(apiURL);

          if (response.ok) {
            const data = await response.json();

            if (data.success === true) {
              setIsSponsorValid(true);
              setSponsorFromUrl(idParam);
              setRegistrationData(prevData => ({
                ...prevData,
                reg_member_sponsor: idParam
              }));
              setBotName(idParam.charAt(0).toUpperCase() + idParam.slice(1));
              showNotification(`Sponsor "${idParam}" valid.`, 'success');
            } else {
              setIsSponsorValid(false);
              setSponsorFromUrl('demoaccount');
              showNotification(`Kode sponsor "${idParam}" tidak valid. Menggunakan sponsor default: demoaccount`, 'info');
            }
          } else {
            // Jika API error, gunakan demoaccount sebagai default
            setIsSponsorValid(true);
            setSponsorFromUrl('demoaccount');
            showNotification('Menggunakan sponsor default: demoaccount', 'info');
          }
        } else {
          // Tidak ada parameter id, gunakan demoaccount
          setIsSponsorValid(true);
          setSponsorFromUrl('demoaccount');
          showNotification('Menggunakan sponsor default: demoaccount', 'info');
        }
      } catch (error) {
        console.error('Error validating sponsor:', error);
        setIsSponsorValid(true);
        setSponsorFromUrl('demoaccount');
        showNotification('Gagal memvalidasi kode sponsor. Menggunakan sponsor default: demoaccount', 'info');
      } finally {
        setIsValidatingSponsor(false);
      }
    };

    checkAndValidateIdParam();
  }, []);

  // Simulate initial messages and set up the first prompt (nama → disimpan sebagai username percakapan)
  useEffect(() => {
    if (!isValidatingSponsor) {
      messageIdRef.current = 0;

      const sponsorLine =
        sponsorFromUrl && sponsorFromUrl !== 'demoaccount'
          ? `Anda direferensikan oleh **${sponsorFromUrl}**.`
          : '';

      const initialMessages = [
        { id: ++messageIdRef.current, text: 'Halo, saya **Mimin**, Admin dari JP Buana ✨', sender: 'bot', type: 'text', hasAvatar: true },
        { id: ++messageIdRef.current, text: 'https://is3.cloudhost.id/scloud/jpbuana/logo-jpbuana.png', sender: 'bot', type: 'image', isCentered: true, hasAvatar: true },
        {
          id: ++messageIdRef.current,
          text:
            `Saya siap membantu Anda mendapatkan informasi seputar **produk**, **peluang bisnis**, **kelas**, dan **dukungan member** JP Buana.${sponsorLine ? `\n\n${sponsorLine}` : ''}`,
          sender: 'bot',
          type: 'text',
          hasAvatar: true,
        },
        { id: ++messageIdRef.current, text: 'Dengan siapa saya berbicara?', sender: 'bot', type: 'text', hasAvatar: true },
        { id: ++messageIdRef.current, text: '', sender: 'user', type: 'prompt', expectsResponse: true, inputPlaceholder: 'Ketik nama Anda di sini...' },
      ];

      setMessages(initialMessages);
    }
  }, [sponsorFromUrl, isValidatingSponsor]);

  // Update activePromptId and scroll to bottom when messages change
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage && typeof lastMessage === 'object' && (
      (lastMessage.type === 'prompt' && lastMessage.expectsResponse) ||
      lastMessage.type === 'form' ||
      lastMessage.type === 'addressForm'
    )) {
      setActivePromptId(lastMessage.id);
      setCurrentPromptInput('');
    } else {
      setActivePromptId(null);
    }
    scrollToBottom();
  }, [messages]);

  // Function to add a bot message with a delay
  const addBotMessageWithDelay = (text, delay) => {
    return new Promise(resolve => {
      setTimeout(() => {
        setMessages(prevMessages => [
          ...prevMessages,
          { id: ++messageIdRef.current, text: text, sender: 'bot', type: 'text', hasAvatar: true }
        ]);
        resolve();
      }, delay);
    });
  };

  // Handle sending a response to a prompt
  const handlePromptResponse = async (promptId, responseText) => {
    if (responseText.trim() === '') return;

    setMessages(prevMessages => prevMessages.map(msg => {
      if (msg.id === promptId) {
        return {
          ...msg,
          expectsResponse: false,
          response: responseText,
          isUserResponse: true,
        };
      }
      return msg;
    }));

    setCurrentPromptInput('');
    setIsBotTyping(true);

    // Simpan nama customer
    setCustomerName(responseText);

    await new Promise(resolve => setTimeout(resolve, 1000));

    const sponsorLine =
      sponsorFromUrl && sponsorFromUrl !== 'demoaccount'
        ? `Anda direferensikan oleh **${sponsorFromUrl}**.`
        : '';

    const finalMenuItems = cartItems.length > 0 ? [...menuItems, cartButton] : menuItems;

    await addBotMessageWithDelay(`Hai **${responseText}**! Senang berkenalan dengan Anda 😊`, 500);
    await addBotMessageWithDelay(buildOpeningHubText(sponsorLine), 500);
    await addBotMessageWithDelay(`Transaksi Anda akan tercatat atas sponsor: **${sponsorFromUrl || 'demoaccount'}**`, 500);
    setMessages(prevMessages => [
      ...prevMessages,
      { id: ++messageIdRef.current, type: 'quickReplyButtons', sender: 'user', items: finalMenuItems },
    ]);
    setCurrentVisibleMenuItems(finalMenuItems);
    setIsBotTyping(false);
  };

  // Handle registration form submission (final step untuk jadi member)
  const handleRegistrationSubmit = async () => {
    const requiredFields = [
      'reg_member_sponsor',
      'reg_member_username',
      'reg_member_name',
      'reg_member_email',
      'reg_member_phone'
    ];

    const isFormValid = requiredFields.every(field =>
      registrationData[field] && registrationData[field].trim() !== ''
    );

    if (!isFormValid) {
      showNotification('Harap isi semua kolom wajib untuk melanjutkan pendaftaran member.', 'error');
      return;
    }

    if (!selectedProduct) {
      showNotification('Produk belum dipilih. Silakan pilih produk terlebih dahulu.', 'error');
      return;
    }

    if (!addressData.street || !addressData.city || !addressData.province || !addressData.postalCode) {
      showNotification('Alamat pengiriman belum lengkap. Silakan lengkapi alamat terlebih dahulu.', 'error');
      return;
    }

    setIsBotTyping(true);

    const payload = {
      reg_member_sponsor: registrationData.reg_member_sponsor,
      reg_member_username: registrationData.reg_member_username,
      reg_member_name: registrationData.reg_member_name,
      reg_member_email: registrationData.reg_member_email,
      reg_member_phone: registrationData.reg_member_phone,
      registration_type: 'with_product',
      product_id: selectedProduct.id,
      product_name: selectedProduct.title,
      product_price: selectedProduct.price,
      address: addressData
    };

    try {
      const response = await fetch('https://api.chat.jpbuana.com/register.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json().catch(() => ({}));

      if (response.ok || responseData.success === true) {
        const summaryText = `📋 **Pendaftaran Member Berhasil!**\n\n` +
          `👤 Nama: ${registrationData.reg_member_name}\n` +
          `📧 Email: ${registrationData.reg_member_email}\n` +
          `📱 Telepon: ${registrationData.reg_member_phone}\n` +
          `🔑 Username: ${registrationData.reg_member_username}\n` +
          `👥 Sponsor: ${registrationData.reg_member_sponsor}\n\n` +
          `📦 **Produk yang dipilih:**\n` +
          `• ${selectedProduct.title}\n` +
          `• Harga: Rp ${selectedProduct.price.toLocaleString('id-ID')}\n\n` +
          `📍 **Alamat Pengiriman:**\n` +
          `${addressData.street}, ${addressData.city}, ${addressData.province}, ${addressData.postalCode}`;

        setMessages(prevMessages => {
          const updatedMessages = prevMessages.map(msg => {
            if (msg.type === 'form' && msg.id === activePromptId) {
              return {
                ...msg,
                type: 'text',
                text: summaryText,
                isUserResponse: true,
                expectsResponse: false,
              };
            }
            return msg;
          });
          return updatedMessages;
        });

        const successMessage =
          '🎉 **Selamat!** Anda resmi menjadi **member JP Buana**.\n\n✨ Tim kami akan segera menghubungi Anda. Anda berhak atas seluruh **bonus & rewards** sesuai ketentuan member.';

        await addBotMessageWithDelay(successMessage, 700);

        setRegistrationData({
          name: '', email: '', phone: '', username: '',
          reg_member_sponsor: sponsorFromUrl,
          reg_member_username: '', reg_member_name: '',
          reg_member_email: '', reg_member_phone: ''
        });
        setAddressData({
          street: '', city: '', province: '', postalCode: '', country: 'Indonesia'
        });
        setSelectedProduct(null);
        setRegistrationStep('product');
        setIsRegistrationFlowActive(false);

        setMessages(prevMessages => [
          ...prevMessages,
          { id: ++messageIdRef.current, text: 'Ada lagi yang bisa **Mimin** bantu? 😊', sender: 'bot', type: 'text', hasAvatar: true },
          { id: ++messageIdRef.current, type: 'quickReplyButtons', sender: 'user', items: menuItems }
        ]);
        setCurrentVisibleMenuItems(menuItems);

      } else {
        const errorMessage = responseData.message || 'Terjadi kesalahan saat mengirim data pendaftaran. Mohon coba lagi nanti.';
        showNotification(errorMessage, 'error');
      }
    } catch (error) {
      console.error('Error submitting registration data:', error);
      showNotification('Terjadi kesalahan jaringan atau server. Mohon coba lagi nanti.', 'error');
    } finally {
      setIsBotTyping(false);
    }
  };

  // Function to add a bot video message with a delay
  const addBotVideoMessageWithDelay = (videoUrl, delay) => {
    return new Promise(resolve => {
      setTimeout(() => {
        setMessages(prevMessages => [
          ...prevMessages,
          { id: ++messageIdRef.current, text: videoUrl, sender: 'bot', type: 'video', hasAvatar: true }
        ]);
        resolve();
      }, delay);
    });
  };

  // Function to add a bot image message with a delay
  const addBotImageMessageWithDelay = (imageUrl, delay) => {
    return new Promise(resolve => {
      setTimeout(() => {
        setMessages(prevMessages => [
          ...prevMessages,
          { id: ++messageIdRef.current, text: imageUrl, sender: 'bot', type: 'image', hasAvatar: true }
        ]);
        resolve();
      }, delay);
    });
  };

  // Function to add a bot product grid message with a delay
  const addBotProductGridMessageWithDelay = (products, delay, returnToItems) => {
    return new Promise(resolve => {
      setTimeout(() => {
        setMessages(prevMessages => [
          ...prevMessages,
          { id: ++messageIdRef.current, sender: 'bot', type: 'productGrid', hasAvatar: true, products: products, returnToItems: returnToItems }
        ]);
        resolve();
      }, delay);
    });
  };

  // Function to add shopping product grid
  const addShoppingProductGrid = async () => {
    setIsBotTyping(true);
    await new Promise(resolve => setTimeout(resolve, 500));

    setMessages(prevMessages => [
      ...prevMessages.filter(msg => msg.type !== 'quickReplyButtons'),
      { id: ++messageIdRef.current, text: '🛍️ Belanja Produk', sender: 'user', type: 'text', isUserResponse: true },
      { id: ++messageIdRef.current, text: `Berikut produk yang tersedia. Transaksi akan tercatat atas sponsor: **${sponsorFromUrl || 'demoaccount'}**`, sender: 'bot', type: 'text', hasAvatar: true },
      {
        id: ++messageIdRef.current,
        sender: 'bot',
        type: 'shoppingGrid',
        hasAvatar: true,
        products: productsList,
        returnToItems: menuItems
      }
    ]);

    setIsBotTyping(false);
  };

  // Function to add a bot product detail message with a delay
  const addBotProductDetailMessageWithDelay = (product, delay, returnToItems) => {
    return new Promise(resolve => {
      setTimeout(() => {
        setMessages(prevMessages => [
          ...prevMessages,
          { id: ++messageIdRef.current, sender: 'bot', type: 'productDetail', hasAvatar: true, product: product, returnToItems: returnToItems }
        ]);
        resolve();
      }, delay);
    });
  };

  // Function to handle product card click (for general product viewing)
  const handleProductCardClick = async (product) => {
    setIsBotTyping(true);
    await new Promise(resolve => setTimeout(resolve, 700));

    setMessages(prevMessages => [
      ...prevMessages.filter(msg => msg.type !== 'quickReplyButtons'),
      { id: ++messageIdRef.current, text: product.title, sender: 'user', type: 'text', isUserResponse: true }
    ]);

    await addBotProductDetailMessageWithDelay(product, 500, [backToProductsButton, mainMenuButton]);

    setMessages(prevMessages => [
      ...prevMessages,
      { id: ++messageIdRef.current, type: 'quickReplyButtons', sender: 'user', items: [backToProductsButton, mainMenuButton] }
    ]);
    setCurrentVisibleMenuItems([backToProductsButton, mainMenuButton]);

    setIsBotTyping(false);
  };

  // Handle shopping product click (add to cart)
  const handleShoppingProductClick = async (product) => {
    setIsBotTyping(true);
    await new Promise(resolve => setTimeout(resolve, 500));

    setMessages(prevMessages => [
      ...prevMessages,
      { id: ++messageIdRef.current, text: product.title, sender: 'user', type: 'text', isUserResponse: true }
    ]);

    // Tampilkan detail produk
    await addBotProductDetailMessageWithDelay(product, 500, [backToProductsButton, mainMenuButton]);

    // Tampilkan tombol beli
    setMessages(prevMessages => [
      ...prevMessages,
      {
        id: ++messageIdRef.current,
        type: 'quickReplyButtons',
        sender: 'user',
        items: [
          { text: '🛒 Tambah ke Keranjang', icon: '🛒', action: 'addToCart', product: product },
          backToProductsButton,
          mainMenuButton
        ]
      }
    ]);
    setCurrentVisibleMenuItems([
      { text: '🛒 Tambah ke Keranjang', icon: '🛒', action: 'addToCart', product: product },
      backToProductsButton,
      mainMenuButton
    ]);

    setIsBotTyping(false);
  };

  // Helper function to format text with newlines, bold, and italic
  const formatText = (text) => {
    if (typeof text !== 'string') return text;

    let formattedText = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    formattedText = formattedText.replace(/\*(.*?)\*/g, '<em>$1</em>');
    formattedText = formattedText.replace(/\n/g, '<br />');

    return <span dangerouslySetInnerHTML={{ __html: formattedText }} />;
  };

  // Handle quick reply menu item click
  const handleMenuItemClick = async (item) => {
    // Handle add to cart action
    if (item.action === 'addToCart' && item.product) {
      addToCart(item.product);
      await addBotMessageWithDelay(`✅ **${item.product.title}** sudah masuk keranjang.`, 500);
      const updatedCartButton = { text: `🛒 Keranjang (${cartItems.length + 1})`, icon: '🛒', action: 'viewCart' };
      setMessages(prevMessages => [
        ...prevMessages,
        { id: ++messageIdRef.current, type: 'quickReplyButtons', sender: 'user', items: [backToProductsButton, mainMenuButton, updatedCartButton] }
      ]);
      setCurrentVisibleMenuItems([backToProductsButton, mainMenuButton, updatedCartButton]);
      setIsBotTyping(false);
      return;
    }

    // Handle view cart action
    if (item.action === 'viewCart') {
      setShowCart(true);
      return;
    }

    // Handle shopping action
    if (item.action === 'shopping') {
      await addShoppingProductGrid();
      return;
    }

    setMessages(prevMessages => {
      const filteredMessages = prevMessages.filter(msg =>
        !(msg.type === 'form' && msg.id === activePromptId) &&
        !(msg.type === 'addressForm' && msg.id === activePromptId)
      );
      const newMessages = filteredMessages.filter(msg => msg.type !== 'quickReplyButtons');
      return [
        ...newMessages,
        { id: ++messageIdRef.current, text: item.text, sender: 'user', type: 'text', isUserResponse: true }
      ];
    });

    setIsBotTyping(true);
    await new Promise(resolve => setTimeout(resolve, 1000));

    if (item.isMainMenu) {
      const targetItems = item.itemsToReturn || menuItems;
      const finalItems = cartItems.length > 0 ? [...targetItems, cartButton] : targetItems;
      setMessages(prevMessages => [
        ...prevMessages,
        { id: ++messageIdRef.current, text: 'Kembali ke **Menu Utama**. Silakan pilih layanan yang Anda butuhkan:', sender: 'bot', type: 'text', hasAvatar: true },
        { id: ++messageIdRef.current, type: 'quickReplyButtons', sender: 'user', items: finalItems }
      ]);
      setCurrentVisibleMenuItems(finalItems);
      setRegistrationData({
        name: '', email: '', phone: '', username: '',
        reg_member_sponsor: sponsorFromUrl,
        reg_member_username: '', reg_member_name: '',
        reg_member_email: '', reg_member_phone: ''
      });
      setAddressData({
        street: '', city: '', province: '', postalCode: '', country: 'Indonesia'
      });
      setSelectedProduct(null);
      setRegistrationStep('product');
      setIsRegistrationFlowActive(false);
      setActivePromptId(null);
    } else if (item.isBack) {
      const targetItems = item.itemsToReturn || menuItems;
      const finalItems = cartItems.length > 0 ? [...targetItems, cartButton] : targetItems;
      setMessages(prevMessages => [
        ...prevMessages,
        { id: ++messageIdRef.current, text: 'Berikut **pilihan produk** JP Buana:', sender: 'bot', type: 'text', hasAvatar: true },
        { id: ++messageIdRef.current, type: 'quickReplyButtons', sender: 'user', items: finalItems }
      ]);
      setCurrentVisibleMenuItems(finalItems);
      setActivePromptId(null);
    } else if (item.action === 'registerWithProduct') {
      await addBotMessageWithDelay(item.response, 500);
      await startRegistrationWithProduct();
    } else if (item.type === 'video') {
      await addBotMessageWithDelay(`📺 **${item.text}**`, 500);
      await addBotVideoMessageWithDelay(item.videoUrl, 500);
      const finalItems = cartItems.length > 0 ? [mainMenuButton, cartButton] : [mainMenuButton];
      setMessages(prevMessages => [
        ...prevMessages,
        { id: ++messageIdRef.current, type: 'quickReplyButtons', sender: 'user', items: finalItems }
      ]);
      setCurrentVisibleMenuItems(finalItems);
    } else if (item.type === 'richContent' && item.contentKey) {
      const activeSponsor = item.sponsorForPanel ?? sponsorFromUrl ?? 'demoaccount';
      let panel;
      if (item.contentKey === 'produk') {
        panel = getProdukShowcasePanel(activeSponsor);
      } else if (item.contentKey === 'workshop') {
        panel = getWorkshopPanel(activeSponsor);
      } else {
        panel = getRichChatPanel(item.contentKey);
      }
      if (panel) {
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            id: ++messageIdRef.current,
            sender: 'bot',
            type: 'richContent',
            content: panel,
            hasAvatar: true,
          },
        ]);
      } else {
        await addBotMessageWithDelay('Maaf, konten ini belum tersedia.', 500);
      }
      if (
        item.productsForGrid &&
        Array.isArray(item.productsForGrid) &&
        item.productsForGrid.length > 0
      ) {
        await new Promise((r) => setTimeout(r, 450));
        await addBotProductGridMessageWithDelay(
          item.productsForGrid,
          500,
          item.productGridReturnToItems ?? []
        );
      }
      const extra = item.afterResponseQuickItems;
      const baseItems =
        Array.isArray(extra) && extra.length ? [...extra, mainMenuButton] : [mainMenuButton];
      const finalItems = cartItems.length > 0 ? [...baseItems, cartButton] : baseItems;
      setMessages((prevMessages) => [
        ...prevMessages,
        { id: ++messageIdRef.current, type: 'quickReplyButtons', sender: 'user', items: finalItems },
      ]);
      setCurrentVisibleMenuItems(finalItems);
    } else if (item.subItems) {
      const botSubText = item.submenuBotIntro != null ? item.submenuBotIntro : item.text;
      setMessages(prevMessages => [
        ...prevMessages,
        { id: ++messageIdRef.current, text: botSubText, sender: 'bot', type: 'text', hasAvatar: true },
        { id: ++messageIdRef.current, type: 'quickReplyButtons', sender: 'user', items: [...item.subItems, mainMenuButton] }
      ]);
      setCurrentVisibleMenuItems([...item.subItems, mainMenuButton]);
    } else if (item.responses) {
      for (const response of item.responses) {
        if (typeof response === 'string') {
          await addBotMessageWithDelay(response, 500);
        } else if (response.type === 'image') {
          await addBotImageMessageWithDelay(response.text, 500);
        } else if (response.type === 'productGrid') {
          await addBotProductGridMessageWithDelay(response.products, 500, response.returnToItems);
          const finalItems = cartItems.length > 0 ? [mainMenuButton, cartButton] : [mainMenuButton];
          setMessages(prevMessages => [
            ...prevMessages,
            { id: ++messageIdRef.current, type: 'quickReplyButtons', sender: 'user', items: finalItems }
          ]);
          setCurrentVisibleMenuItems(finalItems);
          break;
        }
      }
      if (!item.responses.some(response => response.type === 'productGrid')) {
        const finalItems = cartItems.length > 0 ? [mainMenuButton, cartButton] : [mainMenuButton];
        setMessages(prevMessages => [
          ...prevMessages,
          { id: ++messageIdRef.current, type: 'quickReplyButtons', sender: 'user', items: finalItems }
        ]);
        setCurrentVisibleMenuItems(finalItems);
      }
    } else if (item.response) {
      await addBotMessageWithDelay(item.response, 500);
      const extra = item.afterResponseQuickItems;
      const baseItems =
        Array.isArray(extra) && extra.length ? [...extra, mainMenuButton] : [mainMenuButton];
      const finalItems = cartItems.length > 0 ? [...baseItems, cartButton] : baseItems;
      setMessages(prevMessages => [
        ...prevMessages,
        { id: ++messageIdRef.current, type: 'quickReplyButtons', sender: 'user', items: finalItems }
      ]);
      setCurrentVisibleMenuItems(finalItems);
    }

    setIsBotTyping(false);
  };

  // Class dasar untuk semua perangkat
  const baseButtonClass = JPB_TW.btn;

  // Class khusus mobile
  const mobileButtonClass = JPB_TW.btnMobile;

  // Tampilkan loading saat memvalidasi sponsor
  if (isValidatingSponsor) {
    return (
      <div className="flex flex-col items-center justify-center h-screen font-inter" style={{ background: JPB.chatGradient }}>
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-jpb border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p style={{ color: JPB.textSecondary }}>Memuat JP Buana Chat...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen font-inter" style={{ background: JPB.uiNeutral }}>
      {/* Notification Area */}
      {notification && (
        <div className="fixed top-4 right-4 z-50 max-w-sm">
          <Notification
            message={notification.message}
            type={notification.type}
            onClose={closeNotification}
          />
        </div>
      )}

      {/* Shopping Cart Modal */}
      {showCart && (
        <ShoppingCart
          cartItems={cartItems}
          onRemoveItem={removeFromCart}
          onUpdateQuantity={updateQuantity}
          onCheckout={() => {
            setShowCart(false);
            setShowCheckout(true);
          }}
          onClose={() => setShowCart(false)}
          onContinueShopping={() => setShowCart(false)}
        />
      )}

      {/* Checkout Modal */}
      {showCheckout && (
        <CheckoutForm
          cartItems={cartItems}
          onSubmit={handleCheckout}
          onCancel={() => setShowCheckout(false)}
          isSubmitting={isSubmittingOrder}
          sponsorName={sponsorFromUrl || 'demoaccount'}
        />
      )}

      {/* Brand header — selaras jpbuana.com */}
      <header
        className="flex items-center gap-3 px-4 py-3 shadow-sm shrink-0"
        style={{ background: JPB.navy950, color: JPB.textOnDark }}
      >
        <img
          src="https://is3.cloudhost.id/scloud/jpbuana/logo-jpbuana.png"
          alt="JP Buana"
          className="h-8 w-auto object-contain"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <div>
          <div className="text-sm font-semibold leading-tight">JP Buana Chat</div>
          <div className="text-xs opacity-75">Build Your Healthy and Wealthy Life</div>
        </div>
      </header>

      {/* Area Chat */}
      <div className="flex-1 overflow-y-auto p-4" style={{ background: JPB.chatGradient }}>
        <div className="max-w-3xl mx-auto w-full">
          {messages.map((message) => {
            const youtubeEmbedUrl = message.type === 'video' ? getYouTubeEmbedUrl(message.text) : null;
            return (
              <div
                key={message.id}
                className={`flex mb-4 ${message.sender === 'user'
                  ? 'justify-end'
                  : 'justify-start md:justify-start'
                  }`}
              >
                {message.type !== 'quickReplyButtons' ? (
                  <div
                    className={`max-w-full md:max-w-[70%] ${message.sender === 'user' && message.isUserResponse
                      ? 'bg-jpb-light/20 text-jpb-navy rounded-jpb px-4 py-2 text-sm font-medium border border-jpb/10'
                      : (message.type === 'prompt' && message.expectsResponse && activePromptId === message.id) || message.type === 'form' || message.type === 'addressForm'
                        ? 'bg-transparent p-0'
                        : message.type === 'productGrid' || message.type === 'productDetail' || message.type === 'shoppingGrid' || message.type === 'richContent'
                          ? 'bg-transparent p-0'
                          : 'bg-white text-jpb-navy rounded-jpb p-3 shadow-sm border border-jpb/10'
                      }`}
                  >
                    {message.type === 'text' && message.text.trim() !== '' && <p className="text-sm leading-relaxed">{formatText(message.text)}</p>}
                    {message.type === 'image' && (
                      <img
                        src={message.text}
                        alt="Gambar Chat"
                        className="rounded-lg max-w-full h-auto"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/400x150?text=Image+Not+Found';
                        }}
                      />
                    )}
                    {message.type === 'video' && (
                      youtubeEmbedUrl ? (
                        <div className="relative w-full rounded-md overflow-hidden" style={{ paddingTop: '56.25%' }}>
                          <iframe
                            className="absolute top-0 left-0 w-full h-full"
                            src={youtubeEmbedUrl}
                            title="YouTube video player"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                            allowFullScreen
                          ></iframe>
                        </div>
                      ) : (
                        <video controls className="rounded-md w-full h-auto">
                          <source src={message.text} type="video/mp4" />
                          Browser Anda tidak mendukung tag video.
                        </video>
                      )
                    )}
                    {message.type === 'richContent' && message.content != null && (
                      <div className="max-w-[480px] w-full">{message.content}</div>
                    )}
                    {message.type === 'prompt' && (
                      <>
                        {message.expectsResponse && activePromptId === message.id ? (
                          <div className="flex w-full">
                            <input
                              type="text"
                              placeholder={message.inputPlaceholder || 'Ketik jawaban Anda disini...'}
                              className="flex-1 px-4 py-3 bg-white text-gray-800 rounded-md focus:outline-none focus:ring-2 focus:ring-jpb placeholder-gray-500 text-md whitespace-nowrap"
                              value={currentPromptInput}
                              onChange={(e) => setCurrentPromptInput(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  handlePromptResponse(message.id, currentPromptInput);
                                }
                              }}
                            />
                            <button
                              onClick={() => handlePromptResponse(message.id, currentPromptInput)}
                              className="ml-2 p-3 bg-jpb text-white rounded-md hover:bg-jpb-dark focus:outline-none focus:ring-2 focus:ring-jpb transition duration-200 ease-in-out"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                                />
                              </svg>
                            </button>
                          </div>
                        ) : (
                          message.response && message.response.trim() !== '' && <p className="text-sm leading-relaxed">{formatText(message.response)}</p>
                        )}
                      </>
                    )}
                    {/* Formulir Pendaftaran Member (Final Step) */}
                    {message.type === 'form' && activePromptId === message.id && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 bg-white rounded-md">
                        <div className="col-span-1 md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Kode Sponsor *</label>
                          <input
                            type="text"
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-jpb text-gray-800 bg-gray-100 cursor-not-allowed"
                            value={registrationData.reg_member_sponsor || sponsorFromUrl || 'demoaccount'}
                            readOnly
                            disabled
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Sponsor: {sponsorFromUrl || 'demoaccount'} (tidak dapat diubah)
                          </p>
                        </div>

                        <div className="col-span-1 md:col-span-2 relative">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Username Member *</label>
                          <input
                            type="text"
                            placeholder="Masukkan username unik untuk member"
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-jpb text-gray-800 bg-white"
                            value={registrationData.reg_member_username}
                            onChange={handleUsernameChange}
                          />
                          {isSearchingUsername && (
                            <div className="absolute right-3 top-8">
                              <div className="w-4 h-4 border-t-2 border-jpb border-solid rounded-full animate-spin"></div>
                            </div>
                          )}
                          {usernameSearchResults.length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto">
                              {usernameSearchResults.map((result, index) => (
                                <div
                                  key={index}
                                  className="px-4 py-2 cursor-pointer hover:bg-gray-100 border-b border-gray-100"
                                  onClick={() => handleUsernameSelect(result.username)}
                                >
                                  <div className="font-medium">{result.username}</div>
                                  {result.name && <div className="text-xs text-gray-600">{result.name}</div>}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="col-span-1 md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Nama Lengkap *</label>
                          <input
                            type="text"
                            placeholder="Masukkan nama lengkap"
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-jpb text-gray-800 bg-white"
                            value={registrationData.reg_member_name}
                            onChange={(e) => setRegistrationData({ ...registrationData, reg_member_name: e.target.value })}
                          />
                        </div>

                        <div className="col-span-1">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                          <input
                            type="email"
                            placeholder="Masukkan email"
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-jpb text-gray-800 bg-white"
                            value={registrationData.reg_member_email}
                            onChange={(e) => setRegistrationData({ ...registrationData, reg_member_email: e.target.value })}
                          />
                        </div>

                        <div className="col-span-1">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Nomor Telepon *</label>
                          <input
                            type="tel"
                            placeholder="Masukkan nomor telepon aktif"
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-jpb text-gray-800 bg-white"
                            value={registrationData.reg_member_phone}
                            onChange={(e) => setRegistrationData({ ...registrationData, reg_member_phone: e.target.value })}
                          />
                        </div>

                        {selectedProduct && (
                          <div className="col-span-1 md:col-span-2 p-3 bg-jpb-ivory rounded-md border border-jpb/20">
                            <p className="text-sm font-semibold text-jpb-navy-900 mb-1">✅ Produk yang dipilih:</p>
                            <p className="text-sm text-jpb-dark">{selectedProduct.title} - Rp {selectedProduct.price.toLocaleString('id-ID')}</p>
                          </div>
                        )}

                        {addressData.street && addressData.city && (
                          <div className="col-span-1 md:col-span-2 p-3 bg-jpb-ivory rounded-md border border-jpb/20">
                            <p className="text-sm font-semibold text-jpb-navy-900 mb-1">📍 Alamat pengiriman:</p>
                            <p className="text-sm text-jpb-dark">
                              {addressData.street}, {addressData.city}, {addressData.province}, {addressData.postalCode}
                            </p>
                          </div>
                        )}

                        <div className="col-span-1 md:col-span-2 flex flex-col gap-2">
                          <button
                            onClick={handleRegistrationSubmit}
                            className="px-4 py-2 bg-jpb text-white rounded-md hover:bg-jpb-dark focus:outline-none focus:ring-2 focus:ring-jpb transition duration-200 ease-in-out"
                            disabled={isBotTyping}
                          >
                            {isBotTyping ? 'Memproses...' : 'Daftar Member Sekarang'}
                          </button>

                          <button
                            onClick={() => {
                              setSelectedProduct(null);
                              setAddressData({
                                street: '', city: '', province: '', postalCode: '', country: 'Indonesia'
                              });
                              setRegistrationStep('product');
                              startRegistrationWithProduct();
                            }}
                            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 transition duration-200 ease-in-out"
                          >
                            <span className="mr-2 text-lg">🔄</span>Pilih Ulang Produk
                          </button>

                          <button
                            onClick={() => {
                              handleMenuItemClick({ text: 'Kembali', isMainMenu: true, itemsToReturn: menuItems });
                              setRegistrationData({
                                name: '', email: '', phone: '', username: '',
                                reg_member_sponsor: sponsorFromUrl,
                                reg_member_username: '', reg_member_name: '',
                                reg_member_email: '', reg_member_phone: ''
                              });
                              setSelectedProduct(null);
                              setAddressData({
                                street: '', city: '', province: '', postalCode: '', country: 'Indonesia'
                              });
                              setRegistrationStep('product');
                              setIsRegistrationFlowActive(false);
                              setActivePromptId(null);
                            }}
                            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 transition duration-200 ease-in-out"
                          >
                            <span className="mr-2 text-lg">🏠</span>Batalkan & Kembali ke Menu Utama
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Form Alamat untuk Registrasi Member */}
                    {message.type === 'addressForm' && activePromptId === message.id && message.isFromRegistration && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 bg-white rounded-md">
                        <div className="col-span-1 md:col-span-2">
                          <p className="text-sm text-gray-600 mb-2">
                            Silakan isi alamat lengkap untuk pengiriman produk:
                          </p>
                        </div>
                        <input
                          type="text"
                          placeholder="Jalan dan Nomor*"
                          className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-jpb text-gray-800 bg-white"
                          value={addressData.street}
                          onChange={(e) => setAddressData({ ...addressData, street: e.target.value })}
                        />
                        <input
                          type="text"
                          placeholder="Kota*"
                          className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-jpb text-gray-800 bg-white"
                          value={addressData.city}
                          onChange={(e) => setAddressData({ ...addressData, city: e.target.value })}
                        />
                        <input
                          type="text"
                          placeholder="Provinsi*"
                          className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-jpb text-gray-800 bg-white"
                          value={addressData.province}
                          onChange={(e) => setAddressData({ ...addressData, province: e.target.value })}
                        />
                        <input
                          type="text"
                          placeholder="Kode Pos*"
                          className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-jpb text-gray-800 bg-white"
                          value={addressData.postalCode}
                          onChange={(e) => setAddressData({ ...addressData, postalCode: e.target.value })}
                        />
                        <button
                          onClick={handleAddressSubmitForRegistration}
                          className="col-span-1 md:col-span-2 px-4 py-2 bg-jpb text-white rounded-md hover:bg-jpb-dark focus:outline-none focus:ring-2 focus:ring-jpb transition duration-200 ease-in-out"
                          disabled={isBotTyping}
                        >
                          {isBotTyping ? 'Menyimpan...' : 'Simpan Alamat & Lanjutkan'}
                        </button>
                        <button
                          onClick={() => {
                            setSelectedProduct(null);
                            setAddressData({
                              street: '',
                              city: '',
                              province: '',
                              postalCode: '',
                              country: 'Indonesia'
                            });
                            setRegistrationStep('product');
                            startRegistrationWithProduct();
                          }}
                          className="col-span-1 md:col-span-2 px-4 py-2 mt-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 transition duration-200 ease-in-out"
                        >
                          <span className="mr-2 text-lg">⬅️</span>Pilih Ulang Produk
                        </button>
                      </div>
                    )}

                    {/* Product Grid untuk Registrasi Member */}
                    {message.type === 'productGrid' && message.products && message.isRegistrationFlow && (
                      <div className="grid grid-cols-2 gap-4">
                        {message.products.map((product, prodIndex) => (
                          <div
                            key={prodIndex}
                            className="bg-white rounded-xl overflow-hidden cursor-pointer border border-jpb/10 shadow-sm hover:shadow-md hover:border-jpb-light transition duration-200 ease-in-out hover:scale-[1.02]"
                            onClick={() => handleProductSelectForRegistration(product)}
                          >
                            <img
                              src={product.imageUrl}
                              alt={product.title}
                              className="w-full h-auto object-cover"
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/300x200?text=Product+Image';
                              }}
                            />
                            <div className="p-2">
                              <h4 className="font-semibold text-sm mb-1">{product.title}</h4>
                              <p className="text-xs text-gray-600">{product.description}</p>
                              <p className="text-xs font-bold text-jpb mt-1">Rp {product.price.toLocaleString('id-ID')}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Shopping Grid untuk Belanja Biasa (Tanpa Registrasi Member) */}
                    {message.type === 'shoppingGrid' && message.products && (
                      <div>
                        <div className="grid grid-cols-2 gap-4">
                          {message.products.map((product, prodIndex) => (
                            <div
                              key={prodIndex}
                              className="bg-white rounded-xl overflow-hidden cursor-pointer border border-jpb/10 shadow-sm hover:shadow-md hover:border-jpb-light transition duration-200 ease-in-out hover:scale-[1.02]"
                              onClick={() => handleShoppingProductClick(product)}
                            >
                              <img
                                src={product.imageUrl}
                                alt={product.title}
                                className="w-full h-auto object-cover"
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/300x200?text=Product+Image';
                                }}
                              />
                              <div className="p-2">
                                <h4 className="font-semibold text-sm mb-1">{product.title}</h4>
                                <p className="text-xs text-gray-600">{product.description}</p>
                                <p className="text-xs font-bold text-jpb mt-1">Rp {product.price.toLocaleString('id-ID')}</p>
                                <button
                                  className="mt-2 w-full px-2 py-1 bg-jpb text-white text-xs rounded hover:bg-jpb-dark"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    addToCart(product);
                                    showNotification(`✅ ${product.title} ditambahkan ke keranjang!`, 'success');
                                  }}
                                >
                                  🛒 Tambah ke Keranjang
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                        {cartItems.length > 0 && (
                          <div className="mt-4 text-center">
                            <button
                              onClick={() => setShowCart(true)}
                              className="px-4 py-2 bg-jpb text-white rounded-md hover:bg-jpb-dark"
                            >
                              🛒 Lihat Keranjang ({cartItems.length} item)
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Product Grid Umum */}
                    {message.type === 'productGrid' && message.products && !message.isRegistrationFlow && !message.isShoppingFlow && (
                      <div className="grid grid-cols-2 gap-4">
                        {message.products.map((product, prodIndex) => (
                          <div
                            key={prodIndex}
                            className="bg-white rounded-xl overflow-hidden cursor-pointer border border-jpb/10 shadow-sm hover:shadow-md hover:border-jpb-light transition duration-200 ease-in-out hover:scale-[1.02]"
                            onClick={() => handleProductCardClick(product)}
                          >
                            <img
                              src={product.imageUrl}
                              alt={product.title}
                              className="w-full h-auto object-cover"
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/300x200?text=Product+Image';
                              }}
                            />
                            <div className="p-2">
                              <h4 className="font-semibold text-sm mb-1">{product.title}</h4>
                              <p className="text-xs text-gray-600">{product.description}</p>
                              <p className="text-xs font-bold text-jpb mt-1">Rp {product.price.toLocaleString('id-ID')}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Product Detail Rendering */}
                    {message.type === 'productDetail' && message.product && (
                      <div className="flex flex-col gap-3 p-4 bg-white rounded-xl border border-jpb/10 shadow-sm max-w-[480px]">
                        <h3 className="text-xl font-bold text-gray-800">{message.product.title}</h3>
                        <img
                          src={message.product.imageUrl}
                          alt={message.product.title}
                          className="w-full h-48 object-contain rounded-md"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/400x200?text=Product+Detail';
                          }}
                        />
                        <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
                          {formatText(message.product.fullDescription || message.product.description)}
                        </p>
                        <p className="text-lg font-bold text-jpb">Rp {message.product.price.toLocaleString('id-ID')}</p>
                        <button
                          onClick={() => addToCart(message.product)}
                          className="px-4 py-2 bg-jpb text-white rounded-md hover:bg-jpb-dark"
                        >
                          🛒 Tambah ke Keranjang
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-wrap justify-end gap-2 max-w-full md:max-w-[70%]">
                    {message.items.map((item, index) => (
                      <button
                        key={index}
                        onClick={() => handleMenuItemClick(item)}
                        className={isMobile ? mobileButtonClass : baseButtonClass}
                      >
                        {item.icon && <span className="mr-2 text-lg">{item.icon}</span>}
                        {item.text}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Dot Loader untuk bot mengetik */}
          {isBotTyping && (
            <div className="flex mb-4 justify-start">
              <div className="bg-white text-jpb-navy rounded-jpb p-3 flex items-center shadow-sm border border-jpb/10">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Floating Cart Button when cart not empty and cart modal not shown */}
      {cartItems.length > 0 && !showCart && !showCheckout && (
        <button
          onClick={() => setShowCart(true)}
          className="fixed bottom-20 right-4 bg-jpb text-white p-3 rounded-full shadow-lg hover:bg-jpb-dark transition duration-200 z-40"
        >
          <div className="relative">
            <span className="text-xl">🛒</span>
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {cartItems.length}
            </span>
          </div>
        </button>
      )}

      {/* CSS untuk indikator mengetik, spinner, dan notifikasi */}
      <style>{`
        .typing-indicator {
          display: flex;
          align-items: center;
          height: 20px;
        }
        .typing-indicator span {
          width: 6px;
          height: 6px;
          margin: 0 2px;
          background-color: ${JPB.blue700};
          border-radius: 50%;
          opacity: 0.5;
          animation: bounce 1.4s infinite ease-in-out;
        }
        .typing-indicator span:nth-child(2) {
          animation-delay: 0.2s;
        }
        .typing-indicator span:nth-child(3) {
          animation-delay: 0.4s;
        }
        @keyframes bounce {
          0%, 80%, 100% {
            transform: translateY(0);
            opacity: 0.5;
          }
          40% {
            transform: translateY(-5px);
            opacity: 1;
          }
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        
        /* Notification Styles */
        .notification {
          animation: slideIn 0.3s ease-out;
          border-radius: 10px;
          box-shadow: 0 1px 2px rgba(16, 24, 32, 0.04), 0 8px 24px -12px rgba(16, 24, 32, 0.12);
          margin-bottom: 10px;
          max-width: 400px;
        }
        
        .notification.error {
          background-color: #fef2f2;
          border-left: 4px solid #ef4444;
        }
        
        .notification.success {
          background-color: ${JPB.ivory};
          border-left: 4px solid ${JPB.blue700};
        }
        
        .notification.info {
          background-color: ${JPB.warmWhite};
          border-left: 4px solid ${JPB.gold500};
        }
        
        .notification-content {
          display: flex;
          align-items: center;
          padding: 12px 16px;
        }
        
        .notification-icon {
          font-size: 18px;
          margin-right: 12px;
        }
        
        .notification-message {
          flex: 1;
          font-size: 14px;
          color: ${JPB.textPrimary};
        }
        
        .notification-close {
          background: none;
          border: none;
          font-size: 18px;
          cursor: pointer;
          color: ${JPB.textMuted};
          padding: 0;
          margin-left: 8px;
        }
        
        .notification-close:hover {
          color: ${JPB.textPrimary};
        }
        
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default App;